import { CheckCircle2, Clock, MessageSquareReply, XCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { PendingApprovalNotice } from '@/features/approvals';
import { formatDateTime, formatRelativeDate } from '@/shared/lib/format';
import { cn } from '@/shared/lib/utils';

import { complaintOutcome } from '../lib/complaint-outcome';
import type { ComplaintDetails } from '../types/complaint.types';

type Tone = 'warning' | 'info' | 'success' | 'neutral';

const TONE: Record<Tone, string> = {
  warning: 'border-status-warning-border bg-status-warning-bg text-status-warning',
  info: 'border-status-info-border bg-status-info-bg text-status-info',
  success: 'border-status-success-border bg-status-success-bg text-status-success',
  neutral: 'border-status-neutral-border bg-status-neutral-bg text-status-neutral',
};

interface BannerContent {
  tone: Tone;
  icon: LucideIcon;
  title: string;
  detail: string;
}

/**
 * One line that says where the complaint stands and what happens next, so
 * nobody has to piece it together from the status, the thread and History.
 */
export function ComplaintStateBanner({ data }: { data: ComplaintDetails }) {
  // A refund waiting: decide it right here (Approve / Reject when you may —
  // an Admin may approve their own — or Withdraw it if you asked).
  if (data.complaint.status === 'open' && data.pendingRefundApprovalId !== null) {
    return (
      <PendingApprovalNotice approvalId={data.pendingRefundApprovalId} type="Refund">
        <span className="font-semibold">A refund is waiting for approval.</span> The complaint
        closes as resolved with a refund once it is approved.
      </PendingApprovalNotice>
    );
  }

  const content = describe(data);
  const Icon = content.icon;
  return (
    <div
      role="status"
      className={cn('flex items-start gap-3 rounded-lg border px-4 py-3', TONE[content.tone])}
    >
      <Icon className="mt-0.5 size-4 shrink-0" aria-hidden />
      <div className="min-w-0 space-y-0.5">
        <p className="text-sm font-semibold">{content.title}</p>
        <p className="text-xs opacity-90">{content.detail}</p>
      </div>
    </div>
  );
}

function describe(data: ComplaintDetails): BannerContent {
  const complaint = data.complaint;

  if (complaint.status !== 'open') {
    const by = data.modifiedByName ? ` by ${data.modifiedByName}` : '';
    const when = data.modifiedAt ? ` on ${formatDateTime(data.modifiedAt)}` : '';
    return {
      tone: complaint.status === 'resolved' ? 'success' : 'neutral',
      icon: complaint.status === 'resolved' ? CheckCircle2 : XCircle,
      title: complaintOutcome(complaint) ?? complaint.status,
      detail: `Closed${by}${when}. Reopen it if the customer still needs help.`,
    };
  }

  const since = formatRelativeDate(complaint.lastMessageAt ?? complaint.complaintDate);
  const owner = complaint.assigneeName
    ? `${complaint.assigneeName} is handling it.`
    : 'Nobody is handling it yet.';

  if (complaint.awaitingReply) {
    return {
      tone: 'warning',
      icon: MessageSquareReply,
      title: 'The customer is waiting for a reply',
      detail: `They wrote last, ${since}. ${owner}`,
    };
  }

  return {
    tone: 'info',
    icon: Clock,
    title: 'Waiting on the customer',
    detail: `Support replied last, ${since}. ${owner} Resolve or reject it once it is settled.`,
  };
}
